/**
 * Turns whatever failed into wording an Operations user can act on.
 *
 * Every error the app surfaces goes through here (see `notify.error`), so there is exactly one place that decides
 * what an operator reads. The rule the owner set (2026-09-23): never a bare "حصل خطأ" — if we genuinely have no
 * reason, we say that we have none and show the reference so support can trace it.
 *
 * The raw text is never thrown away: it goes to `technical`, behind "تفاصيل فنية" in the card.
 */
import { ApiError } from '@/api/client'
import { explainStatus, explainUpstream, stripCode } from './errorExplain'

export interface ErrorReading {
  /** What failed, in plain Arabic — the card's headline. */
  title: string
  /** Why it failed, in a sentence an operator understands. */
  reason: string
  /** What to do about it, when there is something to do. */
  hint?: string
  /** `true` when trying again can plausibly work (network, timeout, upstream down) — drives the retry button. */
  retryable: boolean
  /** Endpoint / code / reference, for support. Never shown unless the card is expanded. */
  technical: string[]
  /** `false` when we could not find a real reason — the card then says so instead of inventing one. */
  known: boolean
}

/** Dispatch's own validation text (SaveShipmentEx generalResponse / badResponses) when the gateway passed it through. */
export function upstreamMessage(details: unknown): string {
  const data = (details as { data?: unknown } | undefined)?.data
  if (!data || typeof data !== 'object') return ''
  const d = data as { generalResponse?: { message?: string }; badResponses?: { awb?: string; errors?: { message?: string }[] }[] }
  const bad = (d.badResponses ?? []).flatMap((b) => (b.errors ?? []).map((x) => x.message).filter(Boolean).map((m) => (b.awb ? `${b.awb}: ${m}` : m!)))
  return [d.generalResponse?.message, ...bad].filter(Boolean).join(' · ').slice(0, 300)
}

/** Arabic already? Then it came from Dispatch or our own gateway and is meant for a human — show it as the reason. */
const isArabic = (s: string) => /[؀-ۿ]/.test(s)

/**
 * By `ApiError.code` first (the gateway names its own failures), then by HTTP status. Ordered from most specific
 * to least, so a coded error never falls through to a generic status line.
 */
const BY_CODE: Record<string, { title: string; reason: string; hint?: string; retryable?: boolean }> = {
  OFFLINE: { title: 'لا يوجد اتصال بالإنترنت', reason: 'الجهاز غير متصل بالشبكة حاليا، لذلك لم يخرج الطلب من المتصفح.', hint: 'تأكد من اتصال Wi-Fi أو بيانات الهاتف ثم أعد المحاولة.', retryable: true },
  NETWORK_ERROR: { title: 'انقطع الاتصال بالخادم', reason: 'خرج الطلب من الجهاز ولكن لم يصل رد من الخادم — غالبا بسبب انقطاع في الشبكة أو تعذر الوصول إلى الخادم.', hint: 'أعد المحاولة، وإذا تكرر الخطأ فأبلغ الدعم.', retryable: true },
  ABORTED: { title: 'تم إلغاء الطلب', reason: 'توقف الطلب قبل اكتماله — غالبا لأنك أغلقت الصفحة أو انتقلت إلى شاشة أخرى.', retryable: true },
  UPSTREAM_TIMEOUT: { title: 'لم يستجب النظام الرئيسي في الوقت المحدد', reason: 'أرسلنا الطلب إلى نظام Dispatch ولم يصل رد خلال المهلة. قد تكون العملية نفذت لديه وقد لا تكون.', hint: 'راجع الشاشة قبل إعادة العملية لتجنب تكرارها.', retryable: true },
  UPSTREAM_UNAVAILABLE: { title: 'النظام الرئيسي غير متاح', reason: 'نظام Dispatch لا يستجيب للطلبات حاليا.', hint: 'انتظر قليلا ثم أعد المحاولة.', retryable: true },
  UPSTREAM_REJECTED: { title: 'رفض النظام الرئيسي العملية', reason: 'رفض Dispatch الطلب وأعاد سبب الرفض.', retryable: false },
  // not the same as Dispatch being down: the account the system works with is refused, so it needs a human
  DISPATCH_LOGIN_FAILED: {
    title: 'فشل تسجيل الدخول إلى Dispatch',
    reason: 'حساب الخدمة الذي يتصل به النظام مرفوض من Dispatch، لذلك لم تنفذ العملية — المشكلة ليست بطئا أو انقطاعا.',
    hint: 'أبلغ الدعم فورا: يجب تصحيح بيانات الحساب أو صلاحيته على API. إعادة المحاولة لن تفيد قبل ذلك.',
    retryable: false,
  },
  RATE_LIMITED: { title: 'طلبات كثيرة في وقت قصير', reason: 'أرسل عدد من الطلبات أكبر من المسموح في الدقيقة، فأوقف الخادم الباقي مؤقتا.', hint: 'انتظر قليلا ثم أعد المحاولة.', retryable: true },
  VALIDATION_FAILED: { title: 'توجد بيانات ناقصة أو غير صحيحة', reason: 'البيانات المرسلة غير مطابقة للمطلوب، لذلك لم تتم العملية.', hint: 'راجع الحقول المطلوبة وصححها.', retryable: false },
  FORBIDDEN: { title: 'ليست لديك صلاحية لهذه العملية', reason: 'حسابك غير مسموح له بتنفيذ هذه العملية.', hint: 'تواصل مع المسؤول إذا كنت تحتاج هذه الصلاحية.', retryable: false },
  UNAUTHENTICATED: { title: 'انتهت الجلسة', reason: 'يجب تسجيل الدخول مرة أخرى للمتابعة.', retryable: false },
  SESSION_EVICTED: { title: 'تم إغلاق الجلسة', reason: 'تم فتح الحساب من مكان آخر، لذلك أغلقت هذه الجلسة.', hint: 'سجل الدخول مرة أخرى إذا كنت أنت صاحب الحساب.', retryable: false },
  INVALID_RESPONSE: { title: 'رد غير مفهوم من الخادم', reason: 'رد الخادم بشكل غير متوقع، فتعذرت قراءة النتيجة.', retryable: true },
  // The gateway's own assign guard (ext/assignGuard.ts) — its message already names the rule and the waybill.
  ASSIGN_BLOCKED: { title: 'لا يمكن توزيع الشحنة على المندوب', reason: 'توجد شحنة أو أكثر مرفوضة بقاعدة توزيع.', retryable: false },
}

const BY_STATUS: { test: (s: number) => boolean; title: string; reason: string; hint?: string; retryable?: boolean }[] = [
  { test: (s) => s === 400, title: 'الطلب مرفوض', reason: 'وجد الخادم أن بيانات الطلب غير صحيحة.', retryable: false },
  { test: (s) => s === 401, title: 'انتهت الجلسة', reason: 'يجب تسجيل الدخول مرة أخرى للمتابعة.', retryable: false },
  { test: (s) => s === 403, title: 'ليست لديك صلاحية لهذه العملية', reason: 'حسابك غير مسموح له بتنفيذ هذه العملية.', hint: 'تواصل مع المسؤول إذا كنت تحتاج هذه الصلاحية.', retryable: false },
  { test: (s) => s === 404, title: 'العنصر المطلوب غير موجود', reason: 'لم يتم العثور على السجل الذي تحاول فتحه أو تعديله — ربما حذف أو أن الرقم غير صحيح.', retryable: false },
  { test: (s) => s === 409, title: 'يوجد تعارض مع بيانات موجودة', reason: 'تتعارض العملية مع بيانات موجودة بالفعل — ربما عدلها مستخدم آخر قبلك أو أنشئ السجل مسبقا.', hint: 'حدث الصفحة وراجع الوضع الحالي قبل إعادة المحاولة.', retryable: false },
  { test: (s) => s === 422, title: 'البيانات غير مقبولة', reason: 'التنسيق صحيح لكن القيم نفسها غير مقبولة في هذه العملية.', retryable: false },
  { test: (s) => s === 429, title: 'طلبات كثيرة في وقت قصير', reason: 'أرسلت طلبات أكثر من المسموح، فأوقف الخادم الباقي مؤقتا.', hint: 'انتظر قليلا ثم أعد المحاولة.', retryable: true },
  { test: (s) => s === 502 || s === 503 || s === 504, title: 'الخادم غير قادر على الرد حاليا', reason: 'الخدمة المسؤولة عن الطلب متوقفة أو لا تستجيب في الوقت المحدد.', hint: 'انتظر قليلا ثم أعد المحاولة.', retryable: true },
  { test: (s) => s >= 500, title: 'عطل في الخادم', reason: 'حدث خطأ داخلي في الخادم أثناء تنفيذ العملية.', hint: 'أرسل رقم المرجع إلى الدعم إذا تكرر الخطأ.', retryable: true },
]

/** Upstream integrations name themselves in the message — say which one is down instead of "عطل". */
const UPSTREAM_NAMES: [RegExp, string][] = [
  [/dispatch|vsoft/i, 'Dispatch'],
  [/ka3?bo|كعبو/i, 'كعبو'],
  [/enjad|انجاد/i, 'إنجاد'],
  [/line ?express|لاين/i, 'لاين إكسبريس'],
  [/easykash/i, 'EasyKash'],
  [/whatsapp|واتس/i, 'واتساب'],
]

export function readError(error: unknown, fallback?: string): ErrorReading {
  // Several call sites hand us the sentence they want shown rather than an Error ("النسخ مش متاح") — that IS the reason.
  if (typeof error === 'string' && error.trim()) {
    return { title: fallback ?? 'لم تتم العملية', reason: error.trim(), retryable: false, technical: [], known: true }
  }

  const api = error instanceof ApiError ? error : null
  const raw = api?.message ?? (error instanceof Error ? error.message : '') ?? ''
  const upstream = upstreamMessage(api?.details)

  const technical = [
    api ? `HTTP ${api.status} · ${api.code}` : error instanceof Error ? error.name : 'Error',
    raw,
    upstream && upstream !== raw ? upstream : '',
    api?.requestId ? `Ref ${api.requestId}` : '',
  ].filter(Boolean)

  const mapped = api ? BY_CODE[api.code] ?? BY_STATUS.find((r) => r.test(api.status)) : undefined

  /*
   * A rejection somebody has explained (errorExplain.ts) reads as what it means and what to do, with the words that
   * came back kept right under it (owner, 2026-10-21: «مفروض تطلع سبب الايرور لما يرفض عشان الناس تفهم»). The
   * gateway's own crafted answers (assign rules, a refused login, the browser being offline) keep their wording.
   */
  // a coded gateway failure (timeout, offline, rate limit…) already has its exact wording above; the catalog explains
  // what upstream SAID: rejections, bare HTTP codes, and our 403
  if (!api || !BY_CODE[api.code] || api.code === 'UPSTREAM_REJECTED' || api.code === 'FORBIDDEN') {
    const said = stripCode(upstream || raw)
    // our own 403 names the permission it wanted — that beats the generic wording
    const explained = (api?.code === 'FORBIDDEN' ? explainStatus(403, (api.details as { required?: string | string[] } | undefined)?.required) : null) || (said ? explainUpstream(said) : null)
    if (explained) {
      return {
        title: explained.title,
        reason: said ? `${explained.why}\nرسالة النظام: ${said}` : explained.why,
        hint: explained.todo,
        retryable: explained.severity === 'retry' || (api?.isRetryable ?? false),
        technical,
        known: true,
      }
    }
  }

  // Dispatch's own Arabic rejection is the most useful reason there is — it names the actual business rule.
  const human = [upstream, raw].find((s) => s && isArabic(s))
  if (human) {
    const named = UPSTREAM_NAMES.find(([re]) => re.test(raw))?.[1]
    return {
      title: mapped?.title ?? (named ? `${named} رفض العملية` : 'لم تتم العملية'),
      reason: human,
      hint: mapped?.hint,
      retryable: mapped?.retryable ?? api?.isRetryable ?? false,
      technical,
      known: true,
    }
  }

  if (mapped) {
    const named = UPSTREAM_NAMES.find(([re]) => re.test(raw))?.[1]
    const base = named && mapped.title.includes('الرئيسي') ? mapped.reason.replace('Dispatch', named) : mapped.reason
    // An English upstream message still carries the fact: it is shown under the Arabic sentence, not hidden in تفاصيل فنية
    const said = stripCode(raw)
    return {
      title: mapped.title,
      reason: said && api?.code.startsWith('UPSTREAM') ? `${base}\nرسالة النظام: ${said}` : base,
      hint: mapped.hint,
      retryable: mapped.retryable ?? api?.isRetryable ?? false,
      technical,
      known: true,
    }
  }

  // Nothing matched: say exactly that, and lean on the reference instead of pretending to know.
  return {
    title: fallback ?? 'لم تتم العملية',
    reason: raw
      ? `أعاد الخادم هذه الرسالة: ${raw}`
      : api?.requestId
        ? 'لم يرجع الخادم سببا واضحا. أرسل رقم المرجع الموضح أدناه إلى الدعم لتتبع الطلب.'
        : 'لم يرجع الخادم سببا واضحا.',
    retryable: api?.isRetryable ?? false,
    technical,
    known: false,
  }
}
